import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Form from "antd/es/form";
import Row from "antd/es/row";
import Tooltip from "antd/es/tooltip";
import { FormattedMessage, useIntl } from "react-intl";

import CreateDealLossReasonPayload from "@mapmycustomers/shared/types/entity/deals/CreateDealLossReasonPayload";
import DealLossReason from "@mapmycustomers/shared/types/entity/deals/DealLossReason";
import { LoadingSpinner, Modal } from "@mapmycustomers/ui";

import {
  createDealLossReason,
  fetchDealLossReasons,
  updateDealLossReason,
} from "@app/store/dealLossReasons/actions";
import { getDealLossReasons, isDealLossReasonsLoading } from "@app/store/dealLossReasons/selectors";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";

import LossReasonsList from "./LossReasonsList";
import styles from "./ManageDealLossReasonModal.module.scss";

interface Props {
  createDealLossReason: typeof createDealLossReason.request;
  fetchDealLossReasons: typeof fetchDealLossReasons.request;
  isLoading: boolean;
  lossReasons: DealLossReason[];
  onHide: () => void;
  updateDealLossReason: typeof updateDealLossReason.request;
}

const ManageDealLossReasonModal: React.FC<Props> = ({
  createDealLossReason,
  fetchDealLossReasons,
  isLoading,
  lossReasons,
  onHide,
  updateDealLossReason,
}) => {
  const intl = useIntl();
  const [lossReasonsForm] = Form.useForm();
  const [canSave, setCanSave] = useState(false);

  useEffect(() => {
    fetchDealLossReasons();
  }, [fetchDealLossReasons]);

  useEffect(() => {
    lossReasonsForm.setFieldsValue({ lossReasons });
  }, [lossReasons, lossReasonsForm]);

  const handleSaveChanges = useCallback(() => {
    const reasonsList = lossReasonsForm.getFieldValue("lossReasons");
    const reasonsToUpdate: DealLossReason[] = [];
    const reasonsToCreate: CreateDealLossReasonPayload[] = [];
    reasonsList.forEach((reason: CreateDealLossReasonPayload | DealLossReason, index: number) => {
      if ((reason as unknown as DealLossReason).id) {
        reasonsToUpdate.push({
          ...reason,
          displayOrder: index,
        } as unknown as DealLossReason);
      } else {
        reasonsToCreate.push({
          ...reason,
          displayOrder: index,
        } as unknown as CreateDealLossReasonPayload);
      }
    });
    reasonsToUpdate.map((reason) => updateDealLossReason(reason));
    reasonsToCreate.map((reason) => createDealLossReason({ reason }));
    onHide();
  }, [createDealLossReason, lossReasonsForm, updateDealLossReason, onHide]);

  const handleDrop = useCallback(
    (updatedLossReason: DealLossReason[]) => {
      updatedLossReason.forEach((reason) => {
        if (!reason.id) {
          createDealLossReason({ reason });
        }
      });
    },
    [createDealLossReason]
  );

  return (
    <Modal
      className={styles.modalContainer}
      okButtonProps={{
        disabled: !canSave,
      }}
      okText={intl.formatMessage({
        id: "deals.setting.dealLostReasonModal.okButtonText",
        defaultMessage: "Save Changes",
        description: "Ok button text for Deal Lost Reason modal",
      })}
      onCancel={onHide}
      onOk={handleSaveChanges}
      title={intl.formatMessage({
        id: "deals.setting.dealLostReasonModal.title",
        defaultMessage: "Manage Loss Reasons",
        description: "Title for Deal Lost Reason modal",
      })}
      visible
      width="675px"
    >
      <section className={styles.content}>
        <section>
          <Row className={styles.subtitleRow}>
            <FormattedMessage
              id="deals.settings.dealLostReasonModal.subtitle"
              defaultMessage="Measure and report upon deals by requiring your reps to add a reason for a deal to be marked as Closed Lost "
              description="Subtitle for Deal Lost Reason modal"
              tagName="p"
            />
          </Row>
        </section>
        <>
          <Row className={styles.rowHeaders} gutter={[layout.spacerS, layout.spacerS]}>
            <Col className={styles.iconColumn} span={1}>
              <Tooltip
                placement="bottomLeft"
                title={intl.formatMessage({
                  id: "deals.settings.dealLostReasonModal.dropZone.tooltip1",
                  defaultMessage: "Click, hold and drag the grip icon to change order",
                  description: "drag and drop tooltip",
                })}
              >
                <FontAwesomeIcon className={styles.infoIcon} icon={faInfoCircle} />
              </Tooltip>
            </Col>
            <Col span={21}>
              {intl.formatMessage({
                id: "deals.settings.dealLostReasonModal.columns.name",
                defaultMessage: "Name",
                description: "Name column's name in the Deal Loss Reasons management modal",
              })}
            </Col>
            <Col span={1} />
          </Row>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <LossReasonsList
              form={lossReasonsForm}
              lossReasons={lossReasons}
              onDrop={handleDrop}
              onValidate={setCanSave}
            />
          )}
        </>
      </section>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  isLoading: isDealLossReasonsLoading(state),
  lossReasons: getDealLossReasons(state),
});

const mapDispatchToProps = {
  createDealLossReason: createDealLossReason.request,
  fetchDealLossReasons: fetchDealLossReasons.request,
  updateDealLossReason: updateDealLossReason.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageDealLossReasonModal);
